/**
 * RIWAYAT LAPORAN KASIR - FORMAT DETAIL
 * Menampilkan laporan dalam format detail lengkap
 */

import React, { useState, useEffect } from 'react';
import {
  Clock,
  Calendar,
  Filter,
  Search,
  Eye,
  X,
  Trash2,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  MessageCircle,
  FileText,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const RiwayatLaporanKasirDetail = ({ user, api }) => {
  const [loading, setLoading] = useState(true);
  const [laporanList, setLaporanList] = useState([]);
  const [filteredLaporan, setFilteredLaporan] = useState([]);
  const [selectedLaporan, setSelectedLaporan] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 5;

  // Format rupiah
  const formatRupiah = (num) => {
    if (!num && num !== 0) return 'Rp 0';
    return 'Rp ' + new Intl.NumberFormat('id-ID', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  // Parse date - handle extended JSON {"$date": "..."}
  const parseDate = (d) => {
    if (!d) return null;
    if (typeof d === 'object' && d.$date) return new Date(d.$date);
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? null : dt;
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return '-';
    const dt = parseDate(date);
    if (!dt) return '-';
    return dt.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatShiftPeriod = (period) => {
    const map = {
      'PAGI': 'Pagi',
      'SIANG': 'Siang',
      'FULL_DAY': 'Full Day'
    };
    return map[period] || period;
  };

  // Load laporan
  const loadLaporan = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`${api}/api/laporan-kasir/riwayat`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Gagal memuat laporan');
      }

      const result = await response.json();

      if (result.data && Array.isArray(result.data)) {
        setLaporanList(result.data);
        setFilteredLaporan(result.data);
      } else {
        setLaporanList([]);
        setFilteredLaporan([]);
      }
    } catch (err) {
      console.error('Error loading laporan:', err);
      setError('Gagal memuat laporan: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter laporan
  useEffect(() => {
    let filtered = laporanList;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(l =>
        (l.id && l.id.toLowerCase().includes(term)) ||
        (l.shift_period && l.shift_period.toLowerCase().includes(term)) ||
        (l.kasir_name && l.kasir_name.toLowerCase().includes(term))
      );
    }

    setFilteredLaporan(filtered);
    setCurrentPage(1);
  }, [searchTerm, laporanList]);

  useEffect(() => {
    loadLaporan();
  }, []);

  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const paginatedLaporan = filteredLaporan.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredLaporan.length / ITEMS_PER_PAGE);

  const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      let start = Math.max(1, currentPage - 2);
      let end = Math.min(totalPages, currentPage + 2);
      
      if (currentPage <= 3) {
        end = 5;
      } else if (currentPage >= totalPages - 2) {
        start = totalPages - 4;
      }
      
      for (let i = start; i <= end; i++) {
        pageNumbers.push(i);
      }
    }
    return pageNumbers;
  };

  const viewDetail = (laporan) => {
    setSelectedLaporan(laporan);
  };

  const closeDetail = () => {
    setSelectedLaporan(null);
  };

  const shareToWhatsApp = (laporan) => {
    try {
      let message = `💰 *LAPORAN HARIAN KASIR*\n`;
      message += `📅 TGL ${formatDate(laporan.created_at)}\n`;
      message += `👤 Kasir: ${laporan.kasir_name || laporan.cashier_name || '-'}\n`;
      message += `🆔 ID: ${laporan.id}\n\n`;

      // 💵 Setoran Pagi
      if (laporan.setoran_pagi && laporan.setoran_pagi.length > 0) {
        const totalSetoranPagi = (laporan.setoran_pagi || []).reduce((a, b) => a + (b || 0), 0);
        message += `💵 *Setoran Pagi* (Total: ${formatRupiah(totalSetoranPagi)})\n`;
        laporan.setoran_pagi.forEach((item, idx) => {
          if (item > 0) message += `  ${idx + 1}. ${formatRupiah(item)}\n`;
        });
        message += `\n`;
      }

      // 💵 Setoran Siang
      if (laporan.setoran_siang && laporan.setoran_siang.length > 0) {
        const totalSetoranSiang = (laporan.setoran_siang || []).reduce((a, b) => a + (b || 0), 0);
        message += `💵 *Setoran Siang* (Total: ${formatRupiah(totalSetoranSiang)})\n`;
        laporan.setoran_siang.forEach((item, idx) => {
          if (item > 0) message += `  ${idx + 1}. ${formatRupiah(item)}\n`;
        });
        message += `\n`;
      }

      // 🏢 Loket Luar
      const totalLoketLuar = (laporan.deposit_loket_luar || 0) + (laporan.pelunasan_loket_luar_pagi || 0) + (laporan.pelunasan_loket_luar_siang || 0);
      if (totalLoketLuar > 0) {
        message += `🏢 *Loket Luar*\n`;
        if (laporan.deposit_loket_luar > 0) {
          message += `  Deposit: ${formatRupiah(laporan.deposit_loket_luar)}\n`;
        }
        if (laporan.pelunasan_loket_luar_pagi > 0) {
          message += `  Pelunasan Pagi: ${formatRupiah(laporan.pelunasan_loket_luar_pagi)}\n`;
        }
        if (laporan.pelunasan_loket_luar_siang > 0) {
          message += `  Pelunasan Siang: ${formatRupiah(laporan.pelunasan_loket_luar_siang)}\n`;
        }
        message += `  *Total: ${formatRupiah(totalLoketLuar)}*\n\n`;
      }

      // 🔄 Topup Mandiri
      if (laporan.topup_mandiri && laporan.topup_mandiri.some(x => x > 0)) {
        message += `🔄 *Transfer Topup Loket Mandiri*\n`;
        laporan.topup_mandiri.forEach((item, idx) => {
          if (item > 0) {
            message += `  ${idx + 1}. ${formatRupiah(item)}\n`;
          }
        });
        message += `  *Total: ${formatRupiah(laporan.total_topup)}*\n\n`;
      }

      // 💰 Uang Kas Kecil
      message += `💰 *Uang Kas Kecil*\n`;
      message += `  Penerimaan: ${formatRupiah(laporan.penerimaan_kas_kecil || 0)}\n`;
      if (laporan.pengurangan_kas_kecil > 0) {
        message += `  Pengurangan: -${formatRupiah(laporan.pengurangan_kas_kecil)}\n`;
      }
      if (laporan.superpel > 0) {
        const keterangan = laporan.keterangan_superpel || 'Lain-lain';
        message += `  ${keterangan}: ${formatRupiah(laporan.superpel)}\n`;
      }
      message += `  *Total: ${formatRupiah(laporan.total_kas_kecil)}*\n\n`;

      // 👨‍💼 Admin
      message += `👨‍💼 *Admin*\n`;
      message += `  Penerimaan: ${formatRupiah(laporan.penerimaan_admin || 0)}\n`;
      message += `  *Total: ${formatRupiah(laporan.total_admin)}*\n\n`;

      // 🏦 Saldo Akhir
      message += `🏦 *Saldo Akhir*\n`;
      message += `  Bank: ${formatRupiah(laporan.saldo_bank || 0)}\n`;
      message += `  Brangkas: ${formatRupiah(laporan.saldo_brangkas || 0)}\n`;

      // Footer
      message += `\n─────────────────────────\n`;
      message += `📱 *Generated by SIP GELIS V5.0*\n`;
      message += `🤖 Sistem Informasi Pengelolaan Gerai Listrik`;

      const encoded = encodeURIComponent(message);
      window.open(`https://wa.me/?text=${encoded}`, '_blank');
    } catch (err) {
      console.error('Error sharing to WhatsApp:', err);
      setError('Gagal share ke WhatsApp');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Yakin ingin menghapus laporan ini?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${api}/api/laporan-kasir/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Gagal menghapus laporan');
      }

      setSuccess('Laporan berhasil dihapus');
      loadLaporan();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting:', err);
      setError('Gagal menghapus laporan');
      setTimeout(() => setError(''), 3000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="mx-auto mb-4 animate-spin text-blue-600" size={48} />
          <p className="text-gray-600">Memuat laporan...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Riwayat Laporan Kasir</h1>
          <p className="text-gray-600">Daftar laporan kasir harian format detail</p>

          {/* Filters */}
          <div className="mt-6 flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Cari berdasarkan ID, shift, atau kasir..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <button
              onClick={loadLaporan}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <RefreshCw size={18} /> Refresh
            </button>
          </div>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
            <CheckCircle className="text-green-600" size={20} />
            <span className="text-green-700">{success}</span>
            <X
              className="ml-auto cursor-pointer text-green-600"
              size={18}
              onClick={() => setSuccess('')}
            />
          </div>
        )}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="text-red-600" size={20} />
            <span className="text-red-700">{error}</span>
            <X
              className="ml-auto cursor-pointer text-red-600"
              size={18}
              onClick={() => setError('')}
            />
          </div>
        )}

        {/* Laporan List */}
        <div className="space-y-4">
          {filteredLaporan.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
              <FileText size={64} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Belum Ada Laporan</h3>
              <p className="text-gray-500">
                {searchTerm
                  ? 'Tidak ada laporan yang sesuai dengan filter'
                  : 'Mulai buat laporan kasir harian Anda'}
              </p>
            </div>
          ) : (
            paginatedLaporan.map((laporan) => (
              <div
                key={laporan.id}
                className="bg-white rounded-2xl shadow-xl p-6 hover:shadow-2xl transition-shadow"
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  {/* Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-lg font-bold text-gray-800">{laporan.id}</h3>
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        {laporan.id?.startsWith('LD-') ? 'Detail' : 'Simple'}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      <div>
                        <span className="text-gray-600">Tanggal:</span>
                        <p className="font-semibold">{formatDate(laporan.created_at)}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Shift:</span>
                        <p className="font-semibold">{formatShiftPeriod(laporan.shift_period)}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Kasir:</span>
                        <p className="font-semibold">{laporan.kasir_name || laporan.cashier_name || '-'}</p>
                      </div>
                    </div>

                    {/* Ringkasan Lengkap */}
                    <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg space-y-3">
                      {/* Setoran */}
                      <div className="grid grid-cols-2 gap-3 text-sm border-b border-blue-200 pb-2">
                        <div>
                          <span className="text-gray-600">Total Setoran Pagi:</span>
                          <p className="font-bold text-blue-600">
                            {formatRupiah(
                              (laporan.setoran_pagi || []).reduce((a, b) => a + (b || 0), 0)
                            )}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-600">Total Setoran Siang:</span>
                          <p className="font-bold text-blue-600">
                            {formatRupiah(
                              (laporan.setoran_siang || []).reduce((a, b) => a + (b || 0), 0)
                            )}
                          </p>
                        </div>
                      </div>

                      {/* Loket Luar */}
                      {(laporan.deposit_loket_luar > 0 || laporan.pelunasan_loket_luar_pagi > 0 || laporan.pelunasan_loket_luar_siang > 0) && (
                        <div className="text-sm border-b border-green-200 pb-2">
                          <span className="text-gray-600">Loket Luar: </span>
                          <span className="font-semibold text-green-600">
                            {formatRupiah(
                              (laporan.deposit_loket_luar || 0) +
                              (laporan.pelunasan_loket_luar_pagi || 0) +
                              (laporan.pelunasan_loket_luar_siang || 0)
                            )}
                          </span>
                        </div>
                      )}

                      {/* Topup Mandiri */}
                      {laporan.total_topup > 0 && (
                        <div className="text-sm border-b border-purple-200 pb-2">
                          <span className="text-gray-600">Total Topup Mandiri: </span>
                          <span className="font-bold text-purple-600">{formatRupiah(laporan.total_topup)}</span>
                        </div>
                      )}

                      {/* Kas Kecil & Admin */}
                      <div className="grid grid-cols-2 gap-3 text-sm border-b border-yellow-200 pb-2">
                        <div>
                          <span className="text-gray-600">Kas Kecil:</span>
                          <p className="font-bold text-yellow-600">{formatRupiah(laporan.total_kas_kecil || 0)}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Admin:</span>
                          <p className="font-bold text-red-600">{formatRupiah(laporan.total_admin || 0)}</p>
                        </div>
                      </div>

                      {/* Saldo */}
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-600">Saldo Bank:</span>
                          <p className="font-bold text-purple-600">{formatRupiah(laporan.saldo_bank || 0)}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Saldo Brangkas:</span>
                          <p className="font-bold text-purple-600">{formatRupiah(laporan.saldo_brangkas || 0)}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 lg:flex-col lg:gap-2">
                    <button
                      onClick={() => viewDetail(laporan)}
                      className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors flex items-center gap-2"
                    >
                      <Eye size={16} />
                      <span>Detail</span>
                    </button>

                    <button
                      onClick={() => shareToWhatsApp(laporan)}
                      className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors flex items-center gap-2"
                      title="Share ke WhatsApp"
                    >
                      <MessageCircle size={16} />
                      <span>WhatsApp</span>
                    </button>

                    <button
                      onClick={() => handleDelete(laporan.id)}
                      className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors flex items-center gap-2"
                    >
                      <Trash2 size={16} />
                      <span>Hapus</span>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-2xl shadow-xl p-6">
            <div className="text-sm text-gray-600 font-medium">
              Menampilkan {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredLaporan.length)} dari {filteredLaporan.length} laporan
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-2.5 rounded-xl transition-all duration-200 ${
                  currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-50 to-purple-50 text-blue-600 hover:from-blue-100 hover:to-purple-100 border border-blue-100 active:scale-95'
                }`}
                title="Halaman Sebelumnya"
              >
                <ChevronLeft size={18} />
              </button>

              <div className="flex items-center gap-1.5">
                {getPageNumbers().map((page) => (
                  <button
                    key={page}
                    onClick={() => goToPage(page)}
                    className={`w-10 h-10 rounded-xl font-bold transition-all duration-200 active:scale-95 border ${
                      page === currentPage
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white border-blue-700 shadow-md shadow-blue-200'
                        : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-2.5 rounded-xl transition-all duration-200 ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-50 to-purple-50 text-blue-600 hover:from-blue-100 hover:to-purple-100 border border-blue-100 active:scale-95'
                }`}
                title="Halaman Selanjutnya"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}

        {/* Detail Modal */}
        {selectedLaporan && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-auto">
              <div className="p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">Detail Laporan Kasir</h3>
                    <p className="text-gray-600 mt-1">ID: {selectedLaporan.id}</p>
                  </div>
                  <button
                    onClick={closeDetail}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={24} />
                  </button>
                </div>

                {/* Share WhatsApp Button */}
                <div className="mb-4 flex justify-end">
                  <button
                    onClick={() => shareToWhatsApp(selectedLaporan)}
                    className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center gap-2"
                  >
                    <MessageCircle size={20} />
                    Share ke WhatsApp
                  </button>
                </div>

                {/* Informasi Umum */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-lg mb-3">Informasi Umum</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <span className="text-gray-600">Tanggal:</span>
                      <p className="font-semibold">{formatDate(selectedLaporan.created_at)}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Shift:</span>
                      <p className="font-semibold">{formatShiftPeriod(selectedLaporan.shift_period)}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Kasir:</span>
                      <p className="font-semibold">{selectedLaporan.kasir_name || selectedLaporan.cashier_name || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Status:</span>
                      <p className="font-semibold">Draft</p>
                    </div>
                  </div>
                </div>

                {/* Detail Laporan - Format Lengkap */}
                <div className="space-y-4">
                  {/* Setoran */}
                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                    <h4 className="font-semibold text-lg mb-3 text-blue-700">Setoran</h4>

                    {/* Setoran Pagi */}
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">Setoran Pagi:</p>
                      {selectedLaporan.setoran_pagi && selectedLaporan.setoran_pagi.length > 0 ? (
                        <div className="ml-4 space-y-1">
                          {selectedLaporan.setoran_pagi.map((item, idx) => (
                            <p key={idx} className="text-sm">{idx + 1}. {formatRupiah(item || 0)}</p>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm ml-4 text-gray-400">Tidak ada setoran</p>
                      )}
                      <p className="text-sm font-semibold text-blue-600 mt-2 ml-4">
                        Total: {formatRupiah(
                          (selectedLaporan.setoran_pagi || []).reduce((a, b) => a + (b || 0), 0)
                        )}
                      </p>
                    </div>

                    {/* Setoran Siang */}
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Setoran Siang:</p>
                      {selectedLaporan.setoran_siang && selectedLaporan.setoran_siang.length > 0 ? (
                        <div className="ml-4 space-y-1">
                          {selectedLaporan.setoran_siang.map((item, idx) => (
                            <p key={idx} className="text-sm">{idx + 1}. {formatRupiah(item || 0)}</p>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm ml-4 text-gray-400">Tidak ada setoran</p>
                      )}
                      <p className="text-sm font-semibold text-blue-600 mt-2 ml-4">
                        Total: {formatRupiah(
                          (selectedLaporan.setoran_siang || []).reduce((a, b) => a + (b || 0), 0)
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Loket Luar */}
                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                    <h4 className="font-semibold text-lg mb-3 text-green-700">Loket Luar</h4>

                    <div className="space-y-2">
                      <div className="flex justify-between items-center text-sm">
                        <span>Setoran Deposit Loket Luar:</span>
                        <span className="font-semibold">{formatRupiah(selectedLaporan.deposit_loket_luar || 0)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span>Pelunasan Loket Luar Pagi:</span>
                        <span className="font-semibold">{formatRupiah(selectedLaporan.pelunasan_loket_luar_pagi || 0)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span>Pelunasan Loket Luar Siang:</span>
                        <span className="font-semibold">{formatRupiah(selectedLaporan.pelunasan_loket_luar_siang || 0)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm border-t border-green-200 pt-2 mt-2">
                        <span className="font-medium text-green-700">Total Loket Luar:</span>
                        <span className="font-bold text-green-700">
                          {formatRupiah(
                            (selectedLaporan.deposit_loket_luar || 0) +
                            (selectedLaporan.pelunasan_loket_luar_pagi || 0) +
                            (selectedLaporan.pelunasan_loket_luar_siang || 0)
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Topup Mandiri */}
                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                    <h4 className="font-semibold text-lg mb-3 text-purple-700">Transfer Topup Loket Mandiri</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
                      {selectedLaporan.topup_mandiri && selectedLaporan.topup_mandiri.length > 0 ? (
                        selectedLaporan.topup_mandiri.map((item, idx) => (
                          <div key={idx} className="text-sm p-2 bg-gray-50 rounded">
                            <span className="text-gray-600">{idx + 1}.</span>
                            <span className={item > 0 ? "font-semibold text-purple-700" : "text-gray-400"}>
                              {formatRupiah(item || 0)}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-400 col-span-3">Tidak ada data topup</p>
                      )}
                    </div>
                    <div className="flex justify-between items-center border-t border-purple-200 pt-2">
                      <span className="text-sm font-medium text-purple-700">Total Topup:</span>
                      <span className="text-lg font-bold text-purple-700">
                        {formatRupiah(selectedLaporan.total_topup || 0)}
                      </span>
                    </div>
                  </div>

                  {/* Uang Kas Kecil */}
                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                    <h4 className="font-semibold text-lg mb-3 text-yellow-700">Uang Kas Kecil</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center text-sm">
                        <span>Penerimaan Uang Kas Kecil:</span>
                        <span className="font-semibold">{formatRupiah(selectedLaporan.penerimaan_kas_kecil || 0)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span>Pengurangan Uang Kas Kecil (Parkir):</span>
                        <span className="font-semibold text-red-600">
                          -{formatRupiah(selectedLaporan.pengurangan_kas_kecil || 0)}
                        </span>
                      </div>
                      {selectedLaporan.superpel > 0 && (
                        <div className="flex justify-between items-center text-sm">
                          <span>{selectedLaporan.keterangan_superpel || 'Lain-lain'}:</span>
                          <span className="font-semibold">{formatRupiah(selectedLaporan.superpel)}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center text-sm border-t border-yellow-200 pt-2 mt-2">
                        <span className="font-medium text-yellow-700">Total Uang Kas Kecil:</span>
                        <span className="text-lg font-bold text-yellow-700">
                          {formatRupiah(selectedLaporan.total_kas_kecil || 0)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Admin */}
                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                    <h4 className="font-semibold text-lg mb-3 text-red-700">Admin</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center text-sm">
                        <span>Penerimaan Admin:</span>
                        <span className="font-semibold">{formatRupiah(selectedLaporan.penerimaan_admin || 0)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm border-t border-red-200 pt-2 mt-2">
                        <span className="font-medium text-red-700">Total Uang Admin:</span>
                        <span className="text-lg font-bold text-red-700">
                          {formatRupiah(selectedLaporan.total_admin || 0)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Saldo Akhir */}
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border-2 border-purple-200">
                    <h4 className="font-semibold text-lg mb-3">Saldo Akhir</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-gray-700">Saldo Bank:</span>
                        <p className="text-xl font-bold text-purple-600">{formatRupiah(selectedLaporan.saldo_bank || 0)}</p>
                      </div>
                      <div>
                        <span className="text-gray-700">Saldo Brangkas:</span>
                        <p className="text-xl font-bold text-blue-600">{formatRupiah(selectedLaporan.saldo_brangkas || 0)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Close Button */}
                <div className="flex justify-end pt-4 border-t">
                  <button
                    onClick={closeDetail}
                    className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                  >
                    Tutup
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RiwayatLaporanKasirDetail;
